import { mmoSyncSchemaMap } from "@game/config";
import { createMMOServerConnection } from "@services/mmo-game-client/createMMOServerConnection";
import {
  GameAuth,
  GameAuthType,
  MMONetSchema,
  MMORPCError,
  MMORPCParams,
  MMORPCProcedure,
  MMORPCResponse,
  MMOServerEvent,
  MonitoringData,
  RoomMonitoringData,
} from "mmo-common";
import { RealmsEngineClient } from "realms-engine-network-client";
import { TimeSeries } from "./timeSeries";
import { BehaviorSubject } from "rxjs";

export class MonitoringService {
  private numberOfDataPoints = 300;
  private monitoringStatsSubject$ = new BehaviorSubject<{
    currentActivePlayers: number;
    currentNumberOfRooms: number;
  }>({
    currentActivePlayers: 0,
    currentNumberOfRooms: 0,
  });
  readonly monitoringStats$ = this.monitoringStatsSubject$.asObservable();
  readonly roomIds$ = new BehaviorSubject<string[]>([]);

  readonly timeSeries = {
    memory: new TimeSeries<MonitoringData["memory"]>({
      numberOfDataPoints: this.numberOfDataPoints,
    }),
    cpuPercentage: new TimeSeries<MonitoringData["cpuPercentage"]>({
      numberOfDataPoints: this.numberOfDataPoints,
    }),
    activePlayers: new TimeSeries<MonitoringData["currentActivePlayers"]>({
      numberOfDataPoints: this.numberOfDataPoints,
    }),
    averageTicksPerSecond: new TimeSeries<{ [roomId: string]: number }>({
      numberOfDataPoints: this.numberOfDataPoints,
    }),
    roomMonitoringData: new TimeSeries<{
      [roomId: string]: RoomMonitoringData;
    }>({
      numberOfDataPoints: this.numberOfDataPoints,
    }),
  } as const;

  private client = new RealmsEngineClient<MMONetSchema>(
    createMMOServerConnection(),
    mmoSyncSchemaMap,
    {
      ping: false,
      onServerEvent: (event, data) => {
        if (event === MMOServerEvent.MonitoringData) {
          const monitoringData = data as MonitoringData;
          this.addDataPoint(monitoringData);
        }
      },
    }
  );

  private addDataPoint(dataPoint: MonitoringData) {
    // Time Series
    this.timeSeries.memory.addDataPoint({
      value: dataPoint.memory,
      timestamp: dataPoint.timestamp,
    });
    this.timeSeries.cpuPercentage.addDataPoint({
      value: dataPoint.cpuPercentage,
      timestamp: dataPoint.timestamp,
    });
    this.timeSeries.activePlayers.addDataPoint({
      value: dataPoint.currentActivePlayers,
      timestamp: dataPoint.timestamp,
    });
    this.timeSeries.averageTicksPerSecond.addDataPoint({
      value: Object.fromEntries(
        Object.entries(dataPoint.rooms).map(([roomId, data]) => [
          roomId,
          data.averageTicksPerSecond,
        ])
      ),
      timestamp: dataPoint.timestamp,
    });
    this.timeSeries.roomMonitoringData.addDataPoint({
      value: dataPoint.rooms,
      timestamp: dataPoint.timestamp,
    });

    // Subjects
    this.monitoringStatsSubject$.next({
      currentActivePlayers: dataPoint.currentActivePlayers,
      currentNumberOfRooms: dataPoint.currentNumberOfRooms,
    });
    this.roomIds$.next(Array.from(new Set(Object.keys(dataPoint.rooms))));
  }

  mergeDataPoints(data: MonitoringData[]) {
    this.timeSeries.memory.mergeDataPoints(
      data.map((dataPoint) => ({
        value: dataPoint.memory,
        timestamp: dataPoint.timestamp,
      }))
    );
    this.timeSeries.cpuPercentage.mergeDataPoints(
      data.map((dataPoint) => ({
        value: dataPoint.cpuPercentage,
        timestamp: dataPoint.timestamp,
      }))
    );
    this.timeSeries.activePlayers.mergeDataPoints(
      data.map((dataPoint) => ({
        value: dataPoint.currentActivePlayers,
        timestamp: dataPoint.timestamp,
      }))
    );
    this.timeSeries.averageTicksPerSecond.mergeDataPoints(
      data.map((dataPoint) => ({
        value: Object.fromEntries(
          Object.entries(dataPoint.rooms).map(([roomId, data]) => [
            roomId,
            data.averageTicksPerSecond,
          ])
        ),
        timestamp: dataPoint.timestamp,
      }))
    );
    this.timeSeries.roomMonitoringData.mergeDataPoints(
      data.map((dataPoint) => ({
        value: dataPoint.rooms,
        timestamp: dataPoint.timestamp,
      }))
    );
  }

  getClient(): Omit<RealmsEngineClient<MMONetSchema>, "rpc"> & {
    rpc: <T extends MMORPCProcedure>(
      procedure: T,
      params: MMORPCParams<T>
    ) => Promise<MMORPCResponse<T> | MMORPCError>;
  } {
    return this.client;
  }

  setNumberOfDataPoints(dataPoints: number) {
    this.numberOfDataPoints = dataPoints;
    for (const timeSeries of Object.values(this.timeSeries)) {
      timeSeries.numberOfDataPoints = this.numberOfDataPoints;
    }
  }

  getNumberOfDataPoints() {
    return this.numberOfDataPoints;
  }

  /**
   * Connects to monitoring service
   */
  connect(token: string) {
    const auth: GameAuth = {
      authType: GameAuthType.Monitoring,
      token: token,
    };
    this.client.connect(auth);
  }
}
